/**
 * Money arrives from the API in micros — one currency unit is 1,000,000 — so
 * that a sub-cent hourly rate survives the trip. This is the only place it
 * becomes something a person reads.
 */

export const MICROS_PER_UNIT = 1_000_000;

/**
 * A balance or a total, at normal currency precision.
 *
 * Rounds rather than truncates: a balance of 4.999 should read as $5.00, not
 * $4.99, because the customer is about to compare it against what they paid.
 */
export function formatMoney(micros: number, currency = "USD"): string {
  return format(micros / MICROS_PER_UNIT, currency, 2, 2);
}

/**
 * A rate, which may be a fraction of a cent.
 *
 * A $2/month tier is $0.002976 an hour. Two decimal places would print that as
 * $0.00 and read as free, so rates get four — enough to be honest, few enough
 * to read. This was a defensive branch with no live case for a long while, and
 * is not any more: migration 0102 put the entry tiers at $2 and $4, which show
 * as $0.0030 and $0.0060, and an operator can price a tier lower still.
 */
export function formatRate(micros: number, currency = "USD"): string {
  if (micros === 0) return format(0, currency, 2, 2);
  const units = micros / MICROS_PER_UNIT;
  // Below a cent the extra digits are the whole point; above it they are noise.
  return units < 0.01 ? format(units, currency, 4, 4) : format(units, currency, 2, 4);
}

/**
 * What a stretch of AI usage cost, to the micro. Always six decimal places.
 *
 * `formatMoney` rounds to cents, and for a balance or a receipt that is correct.
 * For token spend it is not: a month of light use costs $0.005533, and two
 * decimal places report that as $0.01 — a figure 81% too high, next to a request
 * count in the thousands. Smaller amounts round to $0.00 and read as free when
 * they are not, which is worse, because the customer's balance did move.
 *
 * Six is not a taste: `costMicros` is an integer count of micros, one millionth
 * of a currency unit, so six digits show every charged amount in full and a
 * seventh could not exist.
 *
 * Fixed six rather than "up to six", which is the part worth explaining, because
 * padding adds no information — $0.50 and $0.500000 are the same number, and the
 * trailing zeros say nothing the shorter string did not. What they buy is a
 * column: every figure the same width, decimal points down one line, and a
 * hundredfold difference between two rows visible as a difference in where the
 * digits start rather than as one to be read out of two strings of unequal
 * length. Two of the three places this appears are columns, and the third sits
 * directly above one.
 *
 * The cost of that is real and lands on the totals: a month is a sum of
 * per-request micros and therefore almost never round, so the headline figure
 * reads $12.483917 rather than $12.48. That is the trade this makes deliberately
 * — every digit shown is a digit charged, and the alternative hid four of them.
 */
export function formatUsageCost(micros: number, currency = "USD"): string {
  return format(micros / MICROS_PER_UNIT, currency, 6, 6);
}

function format(value: number, currency: string, min: number, max: number): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      minimumFractionDigits: min,
      maximumFractionDigits: max,
    }).format(value);
  } catch {
    // An operator can configure any currency code, including an invalid one.
    return `${value.toFixed(max)} ${currency}`;
  }
}


/** Whole rupiah the way it is written here: "Rp165.000". No decimals, dots for thousands. */
export function formatRupiah(amount: number): string {
  return `Rp${new Intl.NumberFormat("id-ID", { maximumFractionDigits: 0 }).format(Math.round(amount))}`;
}

/**
 * "QRIS", "BNI virtual account", "DANA" -- from the method the API stored
 * (`qris`, `va:BNI`, `ewallet:DANA`). The virtual-account wording is the
 * caller's, because the bank sits on the other side of the phrase in
 * Indonesian ("Virtual account BNI"); the default is the English document.
 */
export function paymentMethodLabel(
  method: string | null | undefined,
  virtualAccount: (bank: string | null) => string = (bank) => (bank ? `${bank} virtual account` : "virtual account"),
): string | null {
  if (!method) return null;
  const [kind, code] = method.split(":");
  if (kind === "qris") return "QRIS";
  if (kind === "va") return virtualAccount(code ?? null);
  if (kind === "ewallet") return code === "SHOPEE" ? "ShopeePay" : (code ?? "e-wallet");
  return method;
}
